/**
 * Lightweight module that owns just the Auth context object.
 * Kept separate from AuthProvider.tsx so that test utilities can import it
 * without transitively pulling in Firebase SDK init code.
 */
import { createContext, useContext } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { HomeownerModel, StaffModel } from "../types/models";

export interface AuthContextType {
  user: HomeownerModel | StaffModel | null;
  role: string | null;
  setUser: Dispatch<SetStateAction<HomeownerModel | StaffModel | null>>;
  loading: boolean;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | null>(null);

/**
 * Reads the auth state.
 *
 * Lives HERE rather than in AuthProvider.tsx (KAN-622/623): it is nothing but a `useContext`
 * call, and having it on the provider side meant any component reading auth state dragged the
 * Firebase SDK into its module graph -- which throws `auth/invalid-api-key` in a test that has
 * no reason to configure Firebase. That is the exact problem this module was split out to avoid.
 *
 * Still re-exported from AuthProvider, so every existing import keeps working.
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
}
